import React, { useState } from 'react';
import { useAdminState } from '../hooks/useAdminState';
import { useNotificationHelpers } from '../components/NotificationSystem';
import ErrorBoundary from '../components/ErrorBoundary';
import EnhancedLoadingSpinner from '../components/EnhancedLoadingSpinner';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import BillGenerator from '../components/BillGenerator';
import CompanyManager from '../components/CompanyManager';
import CompanyDetailsModal from '../components/CompanyDetailsModal';
import LedgerTable from '../components/LedgerTable';
import ProfitLossChart from '../components/ProfitLossChart';
import ProfitLossLineChart from '../components/ProfitLossLineChart';
import ProfitLossPieChart from '../components/ProfitLossPieChart';
import BillEditModal from '../components/BillEditModal';
import BillManagementModal from '../components/BillManagementModal';
import BillDetailsModal from '../components/BillDetailsModal';
import PaymentForm from '../components/PaymentForm';
import BankStatementUploader from '../components/BankStatementUploader';
import AdminMessaging from '../components/AdminMessaging';
import QuotationGenerator from '../components/QuotationGenerator';
import EditableUTReport from '../components/EditableUTReport';
import EditableTaxInvoice from '../components/EditableTaxInvoice';
import EditableQuotation from '../components/EditableQuotation';
import DeliveryChallanForm from '../components/DeliveryChallanForm';
import DeliveryChallanManagementModal from '../components/DeliveryChallanManagementModal';
import { QuotationRequest } from '../types';
import { BillData, Company, PaymentRecord, DeliveryChallan } from '../types/billing';
import { UltrasonicTestReport } from '../types/ndt';
import { useAuth } from '../contexts/AuthContext';
import { updateExistingBillsWithClientCompanyName } from '../utils/updateExistingBills';
import DeliveryChallanPDF from '../components/DeliveryChallanPDF';
import { PDFViewer, BlobProvider, type BlobProviderParams } from '@react-pdf/renderer';
import { updateQuotation } from '../services/firebaseBilling';
import QuotationPDF from '../components/QuotationPDF';

const AdminBilling: React.FC = () => {
  const DownloadLabel: React.FC<BlobProviderParams> = ({ loading, error }) => (
    <span>{loading ? 'Preparing PDF...' : error ? 'Failed to load PDF' : 'Download PDF'}</span>
  );
  const [showDeliveryChallanPDF, setShowDeliveryChallanPDF] = useState(false);
  const { logout } = useAuth();
  const { showSuccess, showError } = useNotificationHelpers();

  // Use the custom hook for state management
  const {
    // Data
    quotations,
    bills,
    companies,
    payments,
    deliveryChallans,
    // UI State
    isLoading,
    activeTab,
    filterStatus,
    // Bill filtering
    billDateFrom,
    billDateTo,
    billStatusFilter,
    setBillDateFrom,
    setBillDateTo,
    setBillStatusFilter,
    getFilteredBills,
    // Modal States
    showBillGenerator,
    showCompanyManager,
    showPaymentForm,
    showUtReportForm,
    showUtReport,
    showDeliveryChallanForm,
    showDeliveryChallanManagement,
    // Selected Items
    selectedQuotation,
    selectedUtReport,
    selectedCompanyForPayment,
    selectedCompanyForLedger,
    detailsCompanyId,
    editingCompany,
    selectedDeliveryChallan,
    // Analytics
    analyticsRange,
    analyticsCompany,
    ledgerSummary,
    // PDF Generation States
    isGeneratingPDF,
    isSavingBill,
    isSavingCompany,
    isSavingPayment,
    isSavingUtReport,
    // Actions
    setActiveTab,
    setFilterStatus,
    openModal,
    closeModal,
    selectQuotation,
    selectUtReport,
    selectCompanyForPayment,
    selectCompanyForLedger,
    selectCompanyDetails,
    editCompany,
    selectDeliveryChallan,
    saveBill,
    updateBill,
    deleteBill,
    saveCompany,
    updateCompany,
    deleteCompany,
    savePayment,
    addPayment,
    saveDeliveryChallan,
    updateDeliveryChallanAction,
    deleteDeliveryChallanAction,
    markDeliveryChallanAsReturned,
    markDeliveryChallanAsOverdue,
    markDeliveryChallanAsLost,
    updatePayment,
    deletePayment,
    saveUtReport,
    setAnalyticsRange,
    setAnalyticsCompany,
    computeAnalyticsData,
    refreshData,
  } = useAdminState();

  // Local state for chart type
  const [chartType, setChartType] = useState<'bar' | 'line' | 'pie'>('bar');

  // Bill management state
  const [showBillEditModal, setShowBillEditModal] = useState(false);
  const [showBillManagementModal, setShowBillManagementModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState<BillData | null>(null);
  const [selectedBillForDetails, setSelectedBillForDetails] = useState<BillData | null>(null);
  const [showBillDetailsModal, setShowBillDetailsModal] = useState(false);
  const [showMessaging, setShowMessaging] = useState(false);
  const [showEditableTaxInvoice, setShowEditableTaxInvoice] = useState(false);
  const [selectedEditableTaxInvoice, setSelectedEditableTaxInvoice] = useState<BillData | null>(null);
  const [isSavingEditableTaxInvoice, setIsSavingEditableTaxInvoice] = useState(false);
  const [showDeliveryChallanList, setShowDeliveryChallanList] = useState(false);

  // Quotation states
  const [showEditableQuotation, setShowEditableQuotation] = useState(false);
  const [isSavingQuotation, setIsSavingQuotation] = useState(false);

  // Bank statement uploader state
  const [showBankStatementUploader, setShowBankStatementUploader] = useState(false);

  // Quotation generator state
  const [showQuotationGenerator, setShowQuotationGenerator] = useState(false);

  // ✅ NEW: Handler to view a delivery challan
  const handleViewDeliveryChallan = (challan: DeliveryChallan) => {
    selectDeliveryChallan(challan);
    openModal('showDeliveryChallanManagement');
  };

  // Enhanced handlers with error handling and notifications
  const handleSaveCompany = async (company: Company) => {
    try {
      await saveCompany(company);
      showSuccess('Company Saved', 'Company saved successfully');
    } catch (error) {
      showError('Save Failed', 'Failed to save company. Please try again.');
    }
  };

  const handleUpdateCompany = async (company: Company) => {
    try {
      await updateCompany(company);
      showSuccess('Company Updated', 'Company updated successfully');
    } catch (error) {
      showError('Update Failed', 'Failed to update company. Please try again.');
    }
  };

  const handleDeleteCompany = async (companyId: string) => {
    try {
      await deleteCompany(companyId);
      showSuccess('Company Deleted', 'Company deleted successfully');
    } catch (error) {
      showError('Delete Failed', 'Failed to delete company. Please try again.');
    }
  };

  const handleSavePayment = async (payment: PaymentRecord) => {
    try {
      await savePayment(payment);
      showSuccess('Payment Recorded', 'Payment recorded successfully');
    } catch (error) {
      showError('Save Failed', 'Failed to record payment. Please try again.');
    }
  };

  const handleProcessBankStatementPayments = async (payments: PaymentRecord[]) => {
    try {
      // Process all payments in batch
      for (const payment of payments) {
        await savePayment(payment);
      }
      showSuccess('Payments Processed', `Successfully processed ${payments.length} payments from bank statement`);
    } catch (error) {
      showError('Processing Failed', 'Failed to process some payments. Please check the logs.');
    }
  };

  const handleSaveNewQuotation = async (quotationData: any, services?: any[]) => {
    try {
      setIsSavingQuotation(true);
      // Persist status back to the original quotation request, if available
      if (selectedQuotation && selectedQuotation.id) {
        await updateQuotation(selectedQuotation.id, {
          status: 'Approved',
          amount: typeof quotationData?.grandTotal === 'number' ? quotationData.grandTotal : selectedQuotation.amount,
          subject: quotationData?.subject ?? selectedQuotation.subject,
        });
        await refreshData();
      }
      showSuccess('Quotation Generated', 'Quotation has been generated successfully');
      setShowQuotationGenerator(false);
    } catch (error) {
      showError('Generation Failed', 'Failed to generate quotation. Please try again.');
    } finally {
      setIsSavingQuotation(false);
    }
  };

  const handleSaveUtReport = async (report: any) => {
    try {
      await saveUtReport(report);
      showSuccess('Report Saved', 'UT report saved successfully');
    } catch (error) {
      showError('Save Failed', 'Failed to save UT report. Please try again.');
    }
  };

  const handleSaveEditableTaxInvoice = async (invoice: BillData) => {
    try {
      setIsSavingEditableTaxInvoice(true);
      await updateBill(invoice);
      showSuccess('Invoice Updated', 'Tax invoice updated successfully');
    } catch (error) {
      showError('Update Failed', 'Failed to update tax invoice. Please try again.');
    } finally {
      setIsSavingEditableTaxInvoice(false);
    }
  };

  const handleOpenEditableTaxInvoice = (bill: BillData) => {
    setSelectedEditableTaxInvoice(bill);
    setShowEditableTaxInvoice(true);
  };

  const handleSaveQuotation = async (quotationData: any, services: any[]) => {
    try {
      setIsSavingQuotation(true);
      // Here you would typically save to your backend
      console.log('Saving quotation:', quotationData, services);
      showSuccess('Quotation Saved', 'Quotation saved successfully');
    } catch (error) {
      showError('Save Failed', 'Failed to save quotation. Please try again.');
    } finally {
      setIsSavingQuotation(false);
    }
  };

  const handleOpenEditableQuotation = () => {
    setShowEditableQuotation(true);
  };

  const handlePrintBill = async (bill: BillData) => {
    try {
      const { generateBillPDF } = await import('../components/PDFWrapper');
      const blob = await generateBillPDF(bill);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bill-${bill.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showSuccess('Bill Downloaded', 'Bill PDF downloaded successfully');
    } catch (error) {
      showError('Download Failed', 'Failed to download bill PDF. Please try again.');
    }
  };

  const handleUpdateExistingBills = async () => {
    try {
      await updateExistingBillsWithClientCompanyName();
      showSuccess('Update Complete', 'All existing bills have been updated with clientCompanyName field');
      await refreshData(); // Refresh to show updated data
    } catch (error) {
      showError('Update Failed', 'Failed to update existing bills');
    }
  };

  const handleViewBillDetails = (bill: BillData) => {
    setSelectedBillForDetails(bill);
    setShowBillDetailsModal(true);
  };

  const handleOpenEditableUTReport = () => {
    // Create a sample UT report for editing
    const sampleReport: UltrasonicTestReport = {
      id: 'sample-ut-report',
      reportNumber: `MTS/UT/${new Date().getFullYear()}/${String(new Date().getMonth() + 1).padStart(2, '0')}/01`,
      orderNumber: '',
      orderDate: new Date().toISOString().slice(0, 10),
      testDate: new Date().toISOString().slice(0, 10),
      client: {
        name: '',
        address: ''
      },
      subject: 'To find out internal defects / bonding / Lamination',
      jobDescription: 'STEEL ROUND BAR',
      equipment: {
        name: 'Digital Ultrasonic Flaw Detector Einstein-II TFT',
        serialNumber: 'E 3353-0412',
        calibrationDate: '01.04.2022',
        specification: 'ASTM-E-371-90'
      },
      method: 'Pulse - Echo A Scan',
      couplant: 'Oil',
      referenceGainLevel: '---',
      scanningDb: '60 dB',
      evaluationDb: '54 dB',
      referenceStandard: 'ASTMA - 388',
      acceptanceCriteria: '',
      materialGrade: 'C 45',
      heatNumber: '2100077',
      surfaceCondition: 'Rolled',
      basicCalibrationBlock: 'On IIW - V2 Block',
      referenceCalibrationBlock: 'On Test Piece',
      testRange: '0 - 200 mm, 0 - 1000 mm.',
      testDetails: {
        coverage: 'Full Surface Manual Scanning',
        probe: 'M2SN',
        frequency: '2 MHz',
        probeType: 'Normal',
        size: 'Ø 24 mm',
        angle: '0°',
        scanningOverlap: '15%',
        scanDb: '+6dB'
      },
      items: [
        {
          srNo: 1,
          jobDescription: 'STEEL ROUND BAR',
          heatNumber: '2100077',
          quantity: 1,
          observation: 'No defects found',
          remarks: 'Satisfactory'
        }
      ],
      conclusion: 'Tested Ultrasonically & found satisfactory as per above details',
      remark: 'Acceptable as per ASTMA-388',
      inspector: {
        name: 'A.E. Malunjkar',
        certification: 'ASNT-Level-II',
        level: 'UT/MT/PT/VT/RT/ECT/TOFD/PAUT'
      },
      witness: '',
      notes: [
        'In case of rough surface, loss of back wall echo can not be recorded hence not mentioned in the report.',
        'Surface crack propagating from the surface inward in the direction perpendiculars to the tangent drawn at the surface having defect remained undetected. For this, MPT is recommended.'
      ],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    selectUtReport(sampleReport);
    openModal('showUtReport');
  };

  // Bill management handlers
  const handleEditBill = (bill: BillData) => {
    setSelectedBill(bill);
    setShowBillEditModal(true);
  };

  const handleCreateBill = () => {
    setSelectedBill(null);
    setShowBillEditModal(true);
  };

  const handleSaveBill = async (billData: BillData) => {
    try {
      if (selectedBill) {
        await updateBill(billData);
        showSuccess('Bill Updated', 'Bill updated successfully');
      } else {
        await saveBill(billData);
        showSuccess('Bill Created', 'Bill created successfully');
      }
    } catch (error) {
      showError('Save Failed', 'Failed to save bill. Please try again.');
      throw error;
    }
  };

  const handleDeleteBill = async (billId: string) => {
    if (window.confirm('Are you sure you want to delete this bill?')) {
      try {
        await deleteBill(billId);
        showSuccess('Bill Deleted', 'Bill deleted successfully');
      } catch (error) {
        showError('Delete Failed', 'Failed to delete bill. Please try again.');
      }
    }
  };

  const handleManageBill = (bill: BillData) => {
    setSelectedBill(bill);
    setShowBillManagementModal(true);
  };

  // Filter data based on current selections
  const filteredQuotations = quotations.filter(q => 
    filterStatus === 'all' || q.status === filterStatus
  );

  const filteredBills = bills.filter(bill => {
    if (selectedCompanyForLedger === 'all') return true;
    return bill.clientId === selectedCompanyForLedger;
  });

  const filteredPayments = payments.filter(payment => {
    if (selectedCompanyForLedger === 'all') return true;
    return payment.companyId === selectedCompanyForLedger;
  });

  // Table columns configuration
  const quotationColumns = [
    { key: 'clientName', label: 'Company', sortable: true },
    { key: 'serviceType', label: 'Service', sortable: true },
    { key: 'status', label: 'Status', sortable: true, render: (value: string) => (
      <span className={`px-2 py-1 text-xs rounded-full ${
        value === 'Approved' ? 'bg-green-100 text-green-800' :
        value === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
        'bg-red-100 text-red-800'
      }`}>
        {value}
      </span>
    )},
    { key: 'timestamp', label: 'Date', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
    { key: 'actions', label: 'Actions', render: (_: any, item: QuotationRequest) => {
      const openView = () => {
        selectQuotation(item);
        setShowEditableQuotation(true);
      };

      const pdfData = {
        quotationData: {
          refNo: item.invoiceNumber || `QTN-${item.id || ''}`,
          date: new Date(item.timestamp).toLocaleDateString('en-GB'),
          to: item.clientName,
          subject: item.subject || `Quotation for ${item.serviceType}`,
          yourRef: '',
          offerText: item.description || '',
          ourOffer: '',
          amountInWords: '',
          termsAndConditions: [
            'Payment 50% advance and balance after completion.',
            'GST extra as applicable.'
          ],
          closingText: 'We look forward to your order.',
          salutation: 'Thanking you,',
          signature: "AVI'S ENGINEERING & CONSULTANCY"
        },
        services: [
          {
            srNo: '1',
            description: item.description || item.serviceType,
            quantity: '1',
            rate: String(item.amount || 0),
            totalAmount: String(item.amount || 0)
          }
        ]
      };

      if (item.status === 'Pending') {
        return (
          <div className="flex space-x-2">
            <button
              onClick={() => {
                selectQuotation(item);
                setShowQuotationGenerator(true);
              }}
              className="text-avis-blue hover:text-avis-dark text-sm font-medium"
            >
              Generate Quotation
            </button>
          </div>
        );
      }

      return (
        <div className="flex space-x-3 items-center">
          <button
            onClick={openView}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            title="View / Edit"
          >
            View
          </button>
          <BlobProvider document={<QuotationPDF quotationData={pdfData.quotationData} services={pdfData.services} />}>
            {({ url, loading, error }: BlobProviderParams) => (
              <a
                href={url ?? undefined}
                download={`Quotation_${pdfData.quotationData.refNo}.pdf`}
                className={`text-green-600 hover:text-green-800 text-sm font-medium ${loading || error ? 'pointer-events-none opacity-50' : ''}`}
                onClick={(e) => {
                  if (!url || loading || error) e.preventDefault();
                }}
              >
                Download
              </a>
            )}
          </BlobProvider>
        </div>
      );
    }}
  ];

  const companyColumns: any[] = [
    { 
      key: 'name', 
      label: 'Company Name', 
      sortable: true,
      render: (value: string, item: Company) => (
        <div className="flex items-center space-x-3">
          <div className="bg-gradient-to-br from-avis-blue to-avis-dark text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-sm">
            {value.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="font-semibold text-gray-900">{value}</div>
            {item.isDefault && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Default
              </span>
            )}
          </div>
        </div>
      )
    },
    { 
      key: 'email', 
      label: 'Email', 
      sortable: true,
      render: (value: string) => (
        <div className="flex items-center space-x-2">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="text-gray-700">{value}</span>
        </div>
      )
    },
    { 
      key: 'phone', 
      label: 'Phone', 
      sortable: true,
      render: (value: string) => (
        <div className="flex items-center space-x-2">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <span className="text-gray-700">{value}</span>
        </div>
      )
    },
    { 
      key: 'gstn', 
      label: 'GSTN', 
      sortable: true,
      render: (value: string) => (
        <div className="flex items-center space-x-2">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 00-2 2v10a2 2 0 002 2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="text-gray-700 font-mono text-sm">{value}</span>
        </div>
      )
    },
    { 
      key: 'isDefault', 
      label: 'Status', 
      render: (value: boolean) => (
        <div className="flex items-center">
          {value ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Default
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              Regular
            </span>
          )}
        </div>
      )
    },
    { 
      key: 'actions', 
      label: 'Actions', 
      render: (_: any, item: Company) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => {
              editCompany(item);
              openModal('showCompanyManager');
            }}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-avis-blue hover:bg-avis-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-avis-blue transition-colors duration-200"
            title="Edit Company"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </button>
          <button
            onClick={() => selectCompanyDetails(item.id)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            title="View Details"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Details
          </button>
          <button
            onClick={() => handleDeleteCompany(item.id)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
            title="Delete Company"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete
          </button>
        </div>
      )
    }
  ];

  // Format currency as whole number (same as bill PDF - >= 0.50 rounds up, < 0.50 rounds down)
  const formatCurrency = (value: number): string => {
    const rounded = Math.round(value);
    return `₹${rounded.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const billColumns = [
    { key: 'invoiceNumber', label: 'Invoice #', sortable: true },
    { key: 'clientName', label: 'Client', sortable: true },
    { key: 'invoiceDate', label: 'Date', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
    { key: 'totalAmount', label: 'Amount', sortable: true, render: (value: number) => formatCurrency(value) },
    { key: 'status', label: 'Status', sortable: true, render: (value: string) => (
      <span className={`px-2 py-1 text-xs rounded-full ${
        value === 'Paid' ? 'bg-green-100 text-green-800' :
        value === 'Sent' ? 'bg-blue-100 text-blue-800' :
        value === 'Overdue' ? 'bg-red-100 text-red-800' :
        'bg-gray-100 text-gray-800'
      }`}>
        {value}
      </span>
    )},
    { key: 'actions', label: 'Actions', render: (_: any, item: BillData) => (
      <div className="flex space-x-2">
        <button
          onClick={() => handleManageBill(item)}
          className="bg-avis-blue text-white px-3 py-1 rounded text-xs font-medium hover:bg-avis-dark transition-colors"
          title="Manage Bill - Track Payments"
        >
          Manage
        </button>
        <button
          onClick={() => handleEditBill(item)}
          className="text-green-600 hover:text-green-800 text-sm font-medium"
          title="Edit Bill"
        >
          Edit
        </button>
        <button
          onClick={() => handleOpenEditableTaxInvoice(item)}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          title="Edit Tax Invoice"
        >
          Tax Invoice
        </button>
        <button
          onClick={() => handlePrintBill(item)}
          className="text-purple-600 hover:text-purple-800 text-sm font-medium"
          title="Print Bill"
        >
          Print
        </button>
        <button
          onClick={() => handleViewBillDetails(item)}
          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
          title="View Bill Details"
        >
          View
        </button>
        <button
          onClick={() => handleDeleteBill(item.id || '')}
          className="text-red-600 hover:text-red-800 text-sm font-medium"
          title="Delete Bill"
        >
          Delete
        </button>
      </div>
    )}
  ];

  // ✅ Delivery Challan Columns
  const deliveryChallanColumns = [
    { key: 'challanNumber', label: 'Challan #', sortable: true },
    { key: 'challanDate', label: 'Date', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
    { key: 'employeeName', label: 'Employee', sortable: true },
    { key: 'purpose', label: 'Purpose', sortable: true },
    { key: 'status', label: 'Status', sortable: true, render: (value: string) => (
      <span className={`px-2 py-1 text-xs rounded-full ${
        value === 'Active' ? 'bg-blue-100 text-blue-800' :
        value === 'Returned' ? 'bg-green-100 text-green-800' :
        value === 'Overdue' ? 'bg-red-100 text-red-800' :
        value === 'Lost' ? 'bg-gray-100 text-gray-800' :
        'bg-yellow-100 text-yellow-800'
      }`}>
        {value}
      </span>
    )},
    { key: 'actions', label: 'Actions', render: (_: any, item: DeliveryChallan) => (
      <div className="flex space-x-2">
        <button
          onClick={() => handleViewDeliveryChallan(item)} // ✅ Use the new handler
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          title="View Challan"
        >
          View
        </button>
      </div>
    )}
  ];

  if (isLoading) {
    return <EnhancedLoadingSpinner fullScreen text="Loading admin panel..." />;
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-avis-blue to-avis-dark shadow-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-8">
              <div className="flex items-center space-x-4">
                <div className="bg-white p-3 rounded-xl shadow-lg">
                  <svg className="w-8 h-8 text-avis-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-white">Admin Panel</h1>
                  <p className="mt-2 text-lg text-blue-100">Manage your business operations efficiently</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => openModal('showBillGenerator')}
                  className="px-6 py-3 bg-white text-avis-blue rounded-xl hover:bg-blue-50 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Generate Bill</span>
                </button>
                <button
                  onClick={() => openModal('showCompanyManager')}
                  className="px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span>Manage Companies</span>
                </button>
                <button
                  onClick={() => setShowBankStatementUploader(true)}
                  className="px-6 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  <span>Upload Bank Statement</span>
                </button>
                <button
                  onClick={() => setShowQuotationGenerator(true)}
                  className="px-6 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Generate Quotation</span>
                </button>
                <button
                  onClick={logout}
                  className="px-6 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex space-x-1 overflow-x-auto">
              {[
                { id: 'quotations', name: 'Quotations', count: quotations.length, icon: '📋' },
                { id: 'bills', name: 'Bills', count: bills.length, icon: '💰' },
                { id: 'companies', name: 'Companies', count: companies.length, icon: '🏢' },
                { id: 'deliveryChallans', name: 'Delivery Challans', count: deliveryChallans.length, icon: '📦' },
                { id: 'messages', name: 'Messages', count: 0, icon: '💬' },
                { id: 'payments', name: 'Payments', count: payments.length, icon: '💳' },
                { id: 'ledger', name: 'Ledger', count: 0, icon: '📊' },
                { id: 'analytics', name: 'Analytics', count: 0, icon: '📈' },
                { id: 'reports', name: 'Reports', count: 0, icon: '📑' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-6 border-b-3 font-semibold text-sm transition-all duration-200 flex items-center space-x-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-avis-blue text-avis-blue bg-blue-50'
                      : 'border-transparent text-gray-600 hover:text-avis-blue hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-lg">{tab.icon}</span>
                  <span>{tab.name}</span>
                  {tab.count > 0 && (
                    <span className={`ml-2 py-1 px-2 rounded-full text-xs font-bold ${
                      activeTab === tab.id
                        ? 'bg-avis-blue text-white'
                        : 'bg-gray-200 text-gray-700'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Filter Controls */}
          <div className="mb-8 flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  <label className="text-sm font-semibold text-gray-700">Filter by Status:</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="border-2 border-gray-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors font-medium"
                  >
                    <option value="all">All Status</option>
                    <option value="Pending">Pending</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'quotations' && (
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100">
              <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-500 p-2 rounded-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Quotations</h3>
                    <p className="text-sm text-gray-600">Manage quotation requests and convert them to invoices</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <DataTable
                  data={filteredQuotations}
                  columns={quotationColumns}
                  searchable
                  searchPlaceholder="Search quotations..."
                  emptyMessage="No quotations found. Create your first quotation request."
                />
              </div>
            </div>
          )}

          {activeTab === 'bills' && (
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-8 border border-gray-100">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-3">
                    <div className="bg-avis-blue p-3 rounded-xl shadow-lg">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Bills Overview</h3>
                      <p className="text-gray-600">Track your billing performance and outstanding amounts</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-2xl text-white shadow-lg hover:shadow-xl transition-shadow min-h-[140px] overflow-hidden">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-blue-100 uppercase tracking-wide">Total Bills</h4>
                        <p className="text-3xl md:text-4xl font-bold mt-2 leading-tight">{bills.length}</p>
                      </div>
                      <div className="bg-blue-400 p-3 rounded-xl">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-2xl text-white shadow-lg hover:shadow-xl transition-shadow min-h-[140px] overflow-hidden">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-green-100 uppercase tracking-wide">Total Amount</h4>
                        <p className="text-3xl md:text-4xl font-bold mt-2 leading-tight whitespace-nowrap tabular-nums">
                          ₹{Math.round(bills
                            .reduce((sum, bill) => sum + (Number(bill.totalAmount) || 0), 0))
                            .toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </p>
                      </div>
                      <div className="bg-green-400 p-3 rounded-xl">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-2xl text-white shadow-lg hover:shadow-xl transition-shadow min-h-[140px] overflow-hidden">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-orange-100 uppercase tracking-wide">Outstanding</h4>
                        <p className="text-3xl md:text-4xl font-bold mt-2 leading-tight whitespace-nowrap tabular-nums">
                          ₹{Math.round(
                            bills.reduce((sum, bill) => sum + (Number(bill.totalAmount) || 0), 0) -
                            payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0)
                          ).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </p>
                      </div>
                      <div className="bg-orange-400 p-3 rounded-xl">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100">
              <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center space-x-3">
                  <div className="bg-avis-blue p-2 rounded-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">All Bills</h3>
                    <p className="text-sm text-gray-600">Manage and track all your bills</p>
                  </div>
                </div>
                <button
                  onClick={handleCreateBill}
                  className="bg-gradient-to-r from-avis-blue to-avis-dark text-white px-6 py-3 rounded-xl hover:from-avis-dark hover:to-avis-blue transition-all duration-200 font-semibold shadow-lg hover:shadow-xl flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Create New Bill</span>
                </button>
                <button
                  onClick={handleUpdateExistingBills}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium text-sm"
                >
                  Update Existing Bills
                </button>
              </div>
              {/* Bill Filters */}
              <div className="px-8 py-4 border-b border-gray-100 bg-gray-50">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">From:</label>
                    <input
                      type="date"
                      value={billDateFrom}
                      onChange={(e) => setBillDateFrom(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-avis-blue focus:border-avis-blue"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">To:</label>
                    <input
                      type="date"
                      value={billDateTo}
                      onChange={(e) => setBillDateTo(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-avis-blue focus:border-avis-blue"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">Status:</label>
                    <select
                      value={billStatusFilter}
                      onChange={(e) => setBillStatusFilter(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-avis-blue focus:border-avis-blue"
                    >
                      <option value="all">All Status</option>
                      <option value="Draft">Draft</option>
                      <option value="Sent">Sent</option>
                      <option value="Paid">Paid</option>
                    </select>
                  </div>
                  <button
                    onClick={() => {
                      setBillDateFrom('');
                      setBillDateTo('');
                      setBillStatusFilter('all');
                    }}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
              <div className="p-6">
                <DataTable
                  data={getFilteredBills()}
                  columns={billColumns}
                  searchable
                  searchPlaceholder="Search bills..."
                  emptyMessage="No bills found. Create your first bill."
                />
              </div>
            </div>
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100">
              <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-500 p-2 rounded-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Payment Management</h3>
                    <p className="text-sm text-gray-600">Track and manage all payments from clients</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                {payments.length > 0 ? (
                  <div className="space-y-4">
                    {payments.map((payment) => (
                      <div key={payment.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900">
                                  {formatCurrency(payment.amount)}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  {payment.mode} • {new Date(payment.date).toLocaleDateString()}
                                </p>
                                {payment.reference && (
                                  <p className="text-xs text-gray-500">Ref: {payment.reference}</p>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                              payment.mode === 'NEFT' ? 'bg-blue-100 text-blue-800' :
                              payment.mode === 'IMPS' ? 'bg-purple-100 text-purple-800' :
                              payment.mode === 'RTGS' ? 'bg-indigo-100 text-indigo-800' :
                              payment.mode === 'Cash' ? 'bg-green-100 text-green-800' :
                              payment.mode === 'Cheque' ? 'bg-yellow-100 text-yellow-800' :
                              payment.mode === 'UPI' ? 'bg-pink-100 text-pink-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {payment.mode}
                            </span>
                          </div>
                        </div>
                        {payment.notes && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-sm text-gray-600">{payment.notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">💳</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Payments Yet</h3>
                    <p className="text-gray-600 mb-6">Payments received from clients will appear here</p>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-blue-700">
                        💡 <strong>How payments work:</strong><br />
                        • Clients make payments through the payment gateway<br />
                        • Payments are automatically recorded in the system<br />
                        • Bill status is updated to "Paid" when payment is received<br />
                        • All payment records are tracked here for accounting
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'messages' && (
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100">
              <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-500 p-2 rounded-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Client Communication</h2>
                      <p className="text-gray-600">Manage messages and notifications with clients</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowMessaging(true)}
                    className="bg-avis-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-avis-dark transition-colors duration-200 flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span>Open Messages</span>
                  </button>
                </div>
              </div>
              <div className="p-8">
                <div className="text-center py-12 text-gray-500">
                  <div className="text-6xl mb-6">💬</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Client Communication Center</h3>
                  <p className="text-gray-600 mb-6">
                    Click "Open Messages" to access the communication system where you can:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                    <div className="bg-blue-50 p-6 rounded-lg">
                      <div className="text-3xl mb-3">📨</div>
                      <h4 className="font-semibold text-gray-900 mb-2">Send Messages</h4>
                      <p className="text-sm text-gray-600">Communicate directly with clients about their projects</p>
                    </div>
                    <div className="bg-green-50 p-6 rounded-lg">
                      <div className="text-3xl mb-3">📋</div>
                      <h4 className="font-semibold text-gray-900 mb-2">Quote Responses</h4>
                      <p className="text-sm text-gray-600">Respond to quote inquiries and provide updates</p>
                    </div>
                    <div className="bg-yellow-50 p-6 rounded-lg">
                      <div className="text-3xl mb-3">🔔</div>
                      <h4 className="font-semibold text-gray-900 mb-2">Notifications</h4>
                      <p className="text-sm text-gray-600">Send automated notifications for invoices and payments</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'companies' && (
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100">
              <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-500 p-2 rounded-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Companies</h3>
                    <p className="text-sm text-gray-600">Manage your client companies and their information</p>
                  </div>
                </div>
              </div>
              <div className="p-0">
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                  {/* Enhanced Search */}
                  <div className="px-8 py-6 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        placeholder="Search companies by name, email, phone, or GSTN..."
                        className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-lg font-medium"
                      />
                    </div>
                  </div>
                  {/* Enhanced Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-avis-blue to-avis-dark">
                        <tr>
                          {companyColumns.map((column, index) => (
                            <th
                              key={index}
                              className="px-8 py-4 text-left text-sm font-bold text-white uppercase tracking-wider"
                            >
                              <div className="flex items-center space-x-2">
                                <span>{column.label}</span>
                                {column.sortable && (
                                  <svg className="w-4 h-4 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                  </svg>
                                )}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {companies.length === 0 ? (
                          <tr>
                            <td colSpan={companyColumns.length} className="px-8 py-16 text-center">
                              <div className="flex flex-col items-center">
                                <div className="bg-gray-100 p-6 rounded-full mb-4">
                                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                  </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">No companies found</h3>
                                <p className="text-gray-500 mb-6">Get started by adding your first company to the system.</p>
                                <button
                                  onClick={() => openModal('showCompanyManager')}
                                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-avis-blue hover:bg-avis-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-avis-blue transition-colors duration-200"
                                >
                                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                  </svg>
                                  Add First Company
                                </button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          companies.map((company, index) => (
                            <tr
                              key={company.id}
                              className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 group"
                            >
                              {companyColumns.map((column, colIndex) => (
                                <td key={colIndex} className="px-8 py-6 whitespace-nowrap">
                                  {column.render 
                                    ? column.render(company[column.key as keyof Company], company)
                                    : String(company[column.key as keyof Company] || '')
                                  }
                                </td>
                              ))}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {/* Enhanced Footer */}
                  {companies.length > 0 && (
                    <div className="bg-gray-50 px-8 py-4 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <span className="text-sm font-medium text-gray-700">
                            Showing <span className="font-bold text-avis-blue">{companies.length}</span> companies
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openModal('showCompanyManager')}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-avis-blue hover:bg-avis-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-avis-blue transition-colors duration-200"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Add Company
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'deliveryChallans' && (
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100">
              <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-orange-500 p-2 rounded-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M9 9l6 3m-6-3v6m6-3v6" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Delivery Challans</h3>
                      <p className="text-sm text-gray-600">Track instruments and equipment taken by employees</p>
                    </div>
                  </div>
                  <button
                    onClick={() => openModal('showDeliveryChallanForm')}
                    className="px-6 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span>Create Challan</span>
                  </button>
                </div>
              </div>
              <div className="p-6">
                {deliveryChallans.length > 0 ? (
                  <DataTable
                    data={deliveryChallans}
                    columns={deliveryChallanColumns}
                    searchable
                    searchPlaceholder="Search challans..."
                    emptyMessage="No delivery challans found."
                  />
                ) : (
                  <div className="text-center py-16">
                    <div className="bg-orange-100 p-6 rounded-full inline-block mb-4">
                      <svg className="w-12 h-12 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M9 9l6 3m-6-3v6m6-3v6" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Delivery Challan Management</h3>
                    <p className="text-gray-500 mb-6">Track and manage delivery challans for instruments and equipment taken by employees.</p>
                    <button
                      onClick={() => openModal('showDeliveryChallanForm')}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Create First Delivery Challan
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'ledger' && (
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
                <div className="flex justify-between items-center mb-8">
                  <div className="flex items-center space-x-3">
                    <div className="bg-purple-500 p-3 rounded-xl shadow-lg">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Ledger</h3>
                      <p className="text-gray-600">Track financial transactions and balances</p>
                    </div>
                  </div>
                  <select
                    value={selectedCompanyForLedger}
                    onChange={(e) => selectCompanyForLedger(e.target.value)}
                    className="border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors font-medium"
                  >
                    <option value="all">All Companies</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                </div>
                <LedgerTable 
                  summary={ledgerSummary} 
                  company={selectedCompanyForLedger !== 'all' 
                    ? companies.find(c => c.id === selectedCompanyForLedger) 
                    : undefined
                  }
                />
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
                <div className="flex justify-between items-center mb-8">
                  <div className="flex items-center space-x-3">
                    <div className="bg-indigo-500 p-3 rounded-xl shadow-lg">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Analytics</h3>
                      <p className="text-gray-600">Visualize your business performance and trends</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-3">
                      <label className="text-sm font-semibold text-gray-700">Chart Type:</label>
                      <select
                        value={chartType}
                        onChange={(e) => setChartType(e.target.value as 'bar' | 'line' | 'pie')}
                        className="border-2 border-gray-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors font-medium"
                      >
                        <option value="bar">Bar Chart</option>
                        <option value="line">Line Chart</option>
                        <option value="pie">Pie Chart</option>
                      </select>
                    </div>
                    <select
                      value={analyticsCompany}
                      onChange={(e) => setAnalyticsCompany(e.target.value)}
                      className="border-2 border-gray-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors font-medium"
                    >
                      <option value="all">All Companies</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>{company.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  {chartType === 'bar' ? (
                    <ProfitLossChart data={computeAnalyticsData()} />
                  ) : chartType === 'line' ? (
                    <ProfitLossLineChart data={computeAnalyticsData()} />
                  ) : (
                    <ProfitLossPieChart data={computeAnalyticsData()} />
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
                <div className="flex items-center space-x-3 mb-8">
                  <div className="bg-teal-500 p-3 rounded-xl shadow-lg">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Reports</h3>
                    <p className="text-gray-600">Generate and download various business reports</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <button
                    onClick={() => openModal('showUtReportForm')}
                    className="p-6 border-2 border-gray-200 rounded-2xl hover:border-avis-blue hover:bg-blue-50 transition-all duration-200 text-left group"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="bg-blue-100 p-3 rounded-xl group-hover:bg-avis-blue transition-colors">
                        <svg className="w-8 h-8 text-blue-600 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-lg">UT Reports</h4>
                        <p className="text-sm text-gray-600 mt-1">Generate ultrasonic test reports</p>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => handleOpenEditableUTReport()}
                    className="p-6 border-2 border-gray-200 rounded-2xl hover:border-purple-500 hover:bg-purple-50 transition-all duration-200 text-left group"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="bg-purple-100 p-3 rounded-xl group-hover:bg-purple-500 transition-colors">
                        <svg className="w-8 h-8 text-purple-600 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-lg">Editable UT Report</h4>
                        <p className="text-sm text-gray-600 mt-1">Edit and download UT reports</p>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={handleOpenEditableQuotation}
                    className="p-6 border-2 border-gray-200 rounded-2xl hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 text-left group"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="bg-blue-100 p-3 rounded-xl group-hover:bg-blue-500 transition-colors">
                        <svg className="w-8 h-8 text-blue-600 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-lg">Quotation Report</h4>
                        <p className="text-sm text-gray-600 mt-1">Create and edit quotations</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modals */}
        <Modal
          isOpen={showBillGenerator}
          onClose={() => closeModal('showBillGenerator')}
          title="Generate Bill"
          size="2xl"
        >
          <BillGenerator
            companies={companies}
            onSave={handleSaveBill}
            onClose={() => closeModal('showBillGenerator')}
          />
        </Modal>

        <Modal
          isOpen={showCompanyManager}
          onClose={() => closeModal('showCompanyManager')}
          title="Manage Companies"
          size="xl"
        >
          <CompanyManager
            companies={companies}
            onSave={handleSaveCompany}
            onUpdate={handleUpdateCompany}
            onDelete={handleDeleteCompany}
            onClose={() => closeModal('showCompanyManager')}
            editingCompany={editingCompany}
          />
        </Modal>

        <Modal
          isOpen={showPaymentForm}
          onClose={() => closeModal('showPaymentForm')}
          title="Record Payment"
          size="md"
        >
          <PaymentForm
            companies={companies}
            selectedCompanyId={selectedCompanyForPayment}
            onSave={handleSavePayment}
            onCancel={() => closeModal('showPaymentForm')}
          />
        </Modal>

        {detailsCompanyId && (
          <CompanyDetailsModal
            companyId={detailsCompanyId}
            companyName={companies.find(c => c.id === detailsCompanyId)?.name || ''}
            bills={bills.filter(bill => bill.clientId === detailsCompanyId)}
            payments={payments.filter(payment => payment.companyId === detailsCompanyId)}
            onClose={() => selectCompanyDetails(null)}
          />
        )}

        {/* Bill Edit Modal */}
        <BillEditModal
          isOpen={showBillEditModal}
          onClose={() => setShowBillEditModal(false)}
          onSave={handleSaveBill}
          bill={selectedBill}
          companies={companies}
        />

        {/* Bill Management Modal */}
        <BillManagementModal
          isOpen={showBillManagementModal}
          onClose={() => setShowBillManagementModal(false)}
          bill={selectedBill}
          companies={companies}
          onUpdateBill={updateBill}
          onAddPayment={addPayment}
          onUpdatePayment={updatePayment}
          onDeletePayment={deletePayment}
          payments={payments}
        />

        {/* Bill Details Modal */}
        <BillDetailsModal
          isOpen={showBillDetailsModal}
          onClose={() => setShowBillDetailsModal(false)}
          bill={selectedBillForDetails}
          onPrint={handlePrintBill}
        />

        {/* Admin Messaging Modal */}
        <AdminMessaging
          isOpen={showMessaging}
          onClose={() => setShowMessaging(false)}
        />

        {/* Editable UT Report */}
        {showUtReport && selectedUtReport && (
          <EditableUTReport
            report={selectedUtReport}
            onSave={handleSaveUtReport}
            onClose={() => closeModal('showUtReport')}
            isSaving={isSavingUtReport}
          />
        )}

        {/* Editable Tax Invoice */}
        {showEditableTaxInvoice && selectedEditableTaxInvoice && (
          <EditableTaxInvoice
            invoice={selectedEditableTaxInvoice}
            onSave={handleSaveEditableTaxInvoice}
            onClose={() => setShowEditableTaxInvoice(false)}
            isSaving={isSavingEditableTaxInvoice}
          />
        )}

        {/* Editable Quotation */}
        <EditableQuotation
          isOpen={showEditableQuotation}
          onClose={() => setShowEditableQuotation(false)}
          onSave={handleSaveQuotation}
        />

        {/* Bank Statement Uploader */}
        {showBankStatementUploader && (
          <BankStatementUploader
            companies={companies}
            bills={bills}
            onProcessPayments={handleProcessBankStatementPayments}
            onClose={() => setShowBankStatementUploader(false)}
          />
        )}

        {/* Quotation Generator */}
        {showQuotationGenerator && (
          <QuotationGenerator
            companies={companies}
            onSave={handleSaveNewQuotation}
            onClose={() => setShowQuotationGenerator(false)}
            initialData={selectedQuotation ? {
              clientName: selectedQuotation.clientName,
              clientAddress: '', // You might want to add address to QuotationRequest type
              subject: `Quotation for ${selectedQuotation.serviceType}`,
              items: [{
                srNo: 1,
                description: selectedQuotation.description || selectedQuotation.serviceType,
                qty: 1,
                uom: 'NOS',
                rate: selectedQuotation.amount || 0,
                totalAmount: selectedQuotation.amount || 0
              }]
            } : undefined}
            isSaving={isSavingQuotation}
          />
        )}

        {/* Delivery Challan Form */}
        {showDeliveryChallanForm && (
          <DeliveryChallanForm
            onSave={saveDeliveryChallan}
            onCancel={() => closeModal('showDeliveryChallanForm')}
          />
        )}

        {/* Delivery Challan Management Modal */}
        {showDeliveryChallanManagement && selectedDeliveryChallan && (
          <DeliveryChallanManagementModal
            isOpen={showDeliveryChallanManagement}
            onClose={() => closeModal('showDeliveryChallanManagement')}
            challan={selectedDeliveryChallan}
            onUpdateChallan={updateDeliveryChallanAction}
            onMarkAsReturned={markDeliveryChallanAsReturned}
            onMarkAsOverdue={markDeliveryChallanAsOverdue}
            onMarkAsLost={markDeliveryChallanAsLost}
            onDeleteChallan={deleteDeliveryChallanAction}
          />
        )}

        {/* View PDF Button (shown when modal is open and challan is selected) */}
        {showDeliveryChallanManagement && selectedDeliveryChallan && (
          <div className="flex justify-end mt-2">
            <button




              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              onClick={() => setShowDeliveryChallanPDF(true)}
              type="button"
            >
              View PDF
            </button>
          </div>
        )}

        {/* Delivery Challan PDF Modal */}
        {showDeliveryChallanPDF && selectedDeliveryChallan && (
          <Modal
            isOpen={showDeliveryChallanPDF}






























            onClose={() => setShowDeliveryChallanPDF(false)}
            title={`Delivery Challan PDF - #${selectedDeliveryChallan.challanNumber}`}
            size="xl"
          >
      <div style={{ height: 600 }}>
        <PDFViewer width="100%" height="100%">
          <DeliveryChallanPDF challan={selectedDeliveryChallan} />
        </PDFViewer>
      </div>
      <div className="mt-4 flex justify-end space-x-2">
        <BlobProvider document={<DeliveryChallanPDF challan={selectedDeliveryChallan} />}>
          {({ url, loading, error }: BlobProviderParams) => (
            <a
              href={url ?? undefined}
              download={`DeliveryChallan_${selectedDeliveryChallan.challanNumber}.pdf`}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              onClick={(e) => {
                if (!url || loading || error) e.preventDefault();
              }}
            >
              <DownloadLabel loading={loading} error={error} blob={null as any} url={url || ''} />
            </a>
          )}
        </BlobProvider>
      </div>
          </Modal>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default AdminBilling;